import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import AppLayout from '../components/layouts/AppLayout';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle } from 'lucide-react';

export default function ApplicationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/applications/submit', formData);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center px-6" data-testid="application-success">
          <div className="max-w-md w-full glass-panel p-8 text-center">
            <div className="w-16 h-16 bg-[#10B981]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#10B981]" />
            </div>
            <h1 className="font-['Outfit'] text-3xl font-bold mb-4">Application Received!</h1>
            <p className="text-[#94A3B8] mb-8">
              Thank you for your interest. Our team will review your application within 24-48 hours.
              You'll receive an email notification once your application is reviewed.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-[#D4AF37] text-[#0A1128] font-bold rounded-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all"
              data-testid="back-home-btn"
            >
              Back to Home
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="py-12 px-6" data-testid="application-page">
        <button
          onClick={() => navigate('/')}
          className="max-w-4xl mx-auto flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors mb-8 z-20 relative"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>

        <div className="max-w-2xl mx-auto">
        <div className="glass-panel p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#F59E0B] rounded-xl mx-auto mb-4"></div>
            <h1 className="font-['Outfit'] text-4xl font-bold mb-3">Apply for Access</h1>
            <p className="text-[#94A3B8] text-lg">
              Join our exclusive investment platform. Fill out the application below to get started.
            </p>
          </div>

          {/* Benefits */}
          <div className="mb-8 p-6 bg-white/5 rounded-lg border border-white/10">
            <h3 className="font-['Outfit'] text-xl font-bold mb-4">What You'll Get:</h3>
            <ul className="space-y-2 text-[#94A3B8]">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" />
                Access to exclusive investment opportunities
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" />
                Real-time tracking of your portfolio
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" />
                Transparent payout management
              </li>
            </ul>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div data-testid="name-field">
              <label className="block text-sm font-medium mb-2">Full Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
                placeholder="John Doe"
                required
                data-testid="name-input"
              />
            </div>

            <div data-testid="email-field">
              <label className="block text-sm font-medium mb-2">Email Address *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
                placeholder="you@example.com"
                required
                data-testid="email-input"
              />
            </div>

            <div data-testid="phone-field">
              <label className="block text-sm font-medium mb-2">Phone Number</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
                placeholder="+1 (555) 123-4567"
                data-testid="phone-input"
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 px-6 bg-[#D4AF37] text-[#0A1128] font-bold text-lg rounded-lg hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-application-btn"
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>

          {/* Footer Note */}
          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-sm text-[#64748B]">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-[#D4AF37] hover:underline"
                data-testid="goto-login-btn"
              >
                Login here
              </button>
            </p>
          </div>
        </div>
      </div>
      </div>
    </AppLayout>
  );
}
